import { CHUNK_LOAD_ERROR_PATTERN } from "common/utils/app-monitoring/is-chunk-load-notice";

// MN-8798 — stale-deploy code-split recovery.
//
// Lazy-loaded chunks carry a content hash in their filename that rotates on every
// deploy. A client holding a pre-deploy HTML shell (a backgrounded tab, a slow
// connection, an edge-cached page) requests a hash the CDN no longer serves, and
// the dynamic import() rejects with a ChunkLoadError. The infra-side fixes
// (Cache-Control on HTML + longer asset retention) are the primary remedy and live
// outside this repo; this is the front-end defense-in-depth guard the ticket asks
// for. It recovers the user in two escalating, bounded steps:
//
//   1. Retry the import ONCE after a short backoff. A transient network blip or a
//      mid-deploy race (chunk briefly unavailable, then served) self-heals here
//      without disturbing the page.
//   2. If the retry still fails with a chunk error, the chunk is genuinely gone:
//      force a single cache-busting reload to pull the fresh HTML shell (and its
//      new chunk hashes). Two independent signals keep this strictly one-shot — a
//      sessionStorage flag and the `_cb` URL marker the reload itself stamps — so a
//      chunk that is permanently broken can never trap the user in a reload loop,
//      even where sessionStorage is unavailable (private mode / sandboxed iframe).
//
// One-shot guard semantics. The cache-busting reload always lands on a URL carrying
// the `_cb` marker, so a document reached WITHOUT `_cb` is, by definition, not the
// product of our reload — its stale-deploy episode (if any) is over. We therefore
// re-arm the guard once per document — at module load AND on each `turbolinks:load`
// (the storefront uses Turbolinks; a soft navigation swaps the document without
// re-evaluating this module, so a later episode in the same tab could otherwise never
// recover) — gated on `_cb` being absent. This is deliberately NOT done on import
// success: under the PDP below-fold
// `Promise.all`, a fast healthy sibling resolves while a slow still-broken sibling is
// still on its backoff timer; clearing the flag on that success would let the broken
// sibling fire a SECOND reload and re-open the very loop the guard exists to prevent
// (Codex P2 / review feedback). Deciding re-arm from the URL once per document — never
// from a per-import result — keeps the guard race-free across concurrent imports.
//
// Non-chunk rejections (a real bug inside the module) are re-thrown untouched on the
// first attempt so callers' existing error handling and reporting still see them.

export const CHUNK_RELOAD_FLAG = "mn8798:chunk-reload";

// Query param stamped on every cache-busting reload. Doubles as the "this document is
// a post-reload document" marker used to decide whether the guard may be re-armed.
const CACHE_BUST_PARAM = "_cb";

const RETRY_DELAY_MS = 300;

const isChunkLoadError = (error: unknown): boolean => {
  if (error == null) return false;
  const { name } = error as { name?: unknown };
  if (name === "ChunkLoadError") return true;
  const { message } = error as { message?: unknown };
  return typeof message === "string" && CHUNK_LOAD_ERROR_PATTERN.test(message);
};

const safeSessionStorage = (): Storage | null => {
  try {
    return window.sessionStorage;
  } catch {
    // Private-mode / sandboxed iframes can throw on access. With no flag store the
    // reload is bounded instead by the durable `_cb` URL marker (see the reload
    // decision in retryDynamicImport), so a persistent chunk error still cannot loop.
    return null;
  }
};

const hasReloadedThisSession = (): boolean => {
  try {
    return safeSessionStorage()?.getItem(CHUNK_RELOAD_FLAG) === "1";
  } catch {
    // getItem() can throw even when the accessor returned a Storage object
    // (storage-disabled privacy contexts). Treat a read failure as "not reloaded"
    // so the read never aborts the retry path — the durable `_cb` URL marker still
    // bounds the one-shot reload, and the recovery reload itself is not skipped.
    return false;
  }
};

const markReloaded = (): void => {
  try {
    safeSessionStorage()?.setItem(CHUNK_RELOAD_FLAG, "1");
  } catch {
    // Storage may be full or blocked; the reload still happens, it just is not
    // loop-guarded. Acceptable: the failing chunk is far more likely to resolve
    // after one reload than to recur indefinitely.
  }
};

const clearChunkReloadFlag = (): void => {
  try {
    safeSessionStorage()?.removeItem(CHUNK_RELOAD_FLAG);
  } catch {
    /* no-op */
  }
};

// Read the `_cb` marker from the current URL as a tri-state:
//   true  → URL parsed and carries `_cb` (a post-reload document)
//   false → URL parsed and definitively has no `_cb` (a fresh document)
//   null  → URL could not be parsed (malformed href / embedded webview)
// The `null` case matters: the plain-reload() fallback in cacheBustingReload cannot
// stamp `_cb`, so on such a reload we must NOT mistake "unreadable" for "fresh" and
// clear the guard, or the fallback path would loop. Callers distinguish accordingly.
const readCacheBustMarker = (): boolean | null => {
  try {
    return new URL(window.location.href).searchParams.has(CACHE_BUST_PARAM);
  } catch {
    return null;
  }
};

// True only when the URL definitively carries the `_cb` marker (a post-reload
// document). An unreadable URL is treated as "not confirmed" so the FIRST reload is
// still allowed to recover the user.
const isCacheBustDocument = (): boolean => readCacheBustMarker() === true;

const cacheBustingReload = (): void => {
  // Append a one-time query param so the reload bypasses any intermediate HTML
  // cache (browser/edge) that may still hold the stale shell. location.reload()
  // alone can be served from cache; a changed URL forces a fresh document. Use
  // replace() rather than assign() so the broken pre-deploy shell is NOT kept in
  // history — with assign() a Back press returns to the stale no-_cb entry, which
  // re-arms the guard and can bounce the user forward into another recovery reload.
  try {
    const url = new URL(window.location.href);
    url.searchParams.set(CACHE_BUST_PARAM, String(Date.now()));
    window.location.replace(url.toString());
  } catch {
    // URL construction or replace() unavailable (older/embedded webviews): fall
    // back to a plain reload, which still typically re-validates the shell.
    window.location.reload();
  }
};

const delay = (ms: number): Promise<void> =>
  new Promise(resolve => {
    setTimeout(resolve, ms);
  });

/**
 * Re-arm the one-shot reload guard for a fresh document. Clears the guard flag only
 * when the current URL is CONFIRMED to lack the `_cb` marker — meaning this document
 * is not the product of our cache-busting reload, so a future stale deploy may earn
 * its own single reload. On a `_cb` document the flag is left intact so a chunk still
 * broken after the reload cannot loop. Crucially, when the URL cannot be parsed (the
 * embedded-webview case the plain-reload() fallback covers, where `_cb` was never
 * stamped) the flag is also left intact, so the sessionStorage guard still bounds that
 * fallback path to one shot. Runs at module load and on each `turbolinks:load`
 * (below); exported for tests. Idempotent and safe to call repeatedly.
 */
export const rearmChunkReloadGuard = (): void => {
  if (readCacheBustMarker() === false) clearChunkReloadFlag();
};

// Decide re-arm from the URL, never from a per-import result — this is what keeps
// concurrent imports race-free (see the header comment). Run it as the module loads,
// and again on every Turbolinks soft navigation: Turbolinks swaps the document body
// without re-evaluating this module, so without the listener the guard would only ever
// re-arm on a full page load and a second stale-deploy episode in a long-lived tab
// could never earn its own recovery reload. A nav event is single-threaded, so this
// stays race-free.
rearmChunkReloadGuard();
if (typeof document !== "undefined") {
  document.addEventListener("turbolinks:load", rearmChunkReloadGuard);
}

/**
 * Wrap a dynamic-import factory with one-shot retry + a guarded cache-busting
 * reload, so a stale-deploy ChunkLoadError recovers instead of hard-failing the
 * user. Drop-in for a bare `import(...)`:
 *
 *   retryDynamicImport(() => import("components/foo/foo")).then(m => m.default());
 *
 * Resolves with the module on success (first attempt or retry). Re-throws a
 * non-chunk error immediately. On a persistent chunk error it triggers a single
 * reload and the returned promise rejects with the original error (the reload is
 * the recovery, so callers usually do not need to handle the rejection).
 *
 * @template T
 * @param {() => Promise<T>} factory  the dynamic import() call, wrapped in a thunk
 * @returns {Promise<T>}
 */
export async function retryDynamicImport<T>(factory: () => Promise<T>): Promise<T> {
  try {
    return await factory();
  } catch (firstError) {
    if (!isChunkLoadError(firstError)) throw firstError;

    await delay(RETRY_DELAY_MS);

    try {
      return await factory();
    } catch (secondError) {
      // Bound the reload to one shot using two independent signals: the
      // sessionStorage flag AND the `_cb` URL marker. The URL marker is durable even
      // where sessionStorage is unavailable (private mode / sandboxed iframe), so a
      // permanently-gone chunk cannot loop the reload across navigations there.
      if (isChunkLoadError(secondError) && !hasReloadedThisSession() && !isCacheBustDocument()) {
        markReloaded();
        cacheBustingReload();
      }
      throw secondError;
    }
  }
}
